import { INTERPRETER_BASE_URL, VISUALIZER_BASE_URL } from '../config/runtime';
import { v4 as uuidv4 } from 'uuid';

const STORAGE_KEY = 'browserSessionId';
let pageSessionId = null;

function updateEmptyUserId(sessionId, userIdValue) {
  if (!sessionId || userIdValue === 'anonymous') return sessionId;

  const sep = sessionId.indexOf('::');
  if (sep <= 0) return sessionId;

  const prefix = sessionId.slice(0, sep);
  const suffix = sessionId.slice(sep + 2);
  if (prefix !== 'anonymous' || !suffix) return sessionId;

  const updated = `${userIdValue}::${suffix}`;
  sessionStorage.setItem(STORAGE_KEY, updated);
  return updated;
}

// TODO: In the future, sessionId will come from elsewhere (session id from auth?)
export const getSessionId = (userId = null) => {
  try {
    const userIdValue = String(userId?.id ?? userId ?? 'anonymous');

    if (pageSessionId === null) {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      const navType = performance.getEntriesByType('navigation')[0]?.type ?? 'navigate';
      const reuse = stored && (navType === 'reload' || navType === 'back_forward');

      if (reuse) {
        pageSessionId = stored;
      } else if (stored) {
        const sep = stored.indexOf('::');
        const storedPrefix = sep > 0 ? stored.slice(0, sep) : userIdValue;
        const prefix = storedPrefix !== 'anonymous' ? storedPrefix : userIdValue;
        pageSessionId = `${prefix}::${uuidv4()}`;
        sessionStorage.setItem(STORAGE_KEY, pageSessionId);
      } else {
        pageSessionId = `${userIdValue}::${uuidv4()}`;
        sessionStorage.setItem(STORAGE_KEY, pageSessionId);
      }
    }

    pageSessionId = updateEmptyUserId(pageSessionId, userIdValue);
    return pageSessionId;
  } catch (error) {
    console.error('[ERROR] getSessionId failed:', error);
    const userIdValue = String(userId?.id ?? userId ?? 'anonymous');
    return `${userIdValue}::${uuidv4()}`;
  }
};

export const visualizeService = {
  /**
   * Send a DPM visualization request to the backend
   * @param {string} dpmFilename - The DPM filename to visualize
   * @param {string} userId - The user ID
   * @param {string|null} [dpmUuid] - Optional DPM UUID
   * @returns {Promise<Object>} - The response from the API
   */
  async visualizeDpm(dpmFilename, userId, dpmUuid = null) {
    try {
      const sessionId = getSessionId(userId);
      console.log('[DPMVisualize] dpmFilename:', dpmFilename, 'dpmUuid:', dpmUuid ?? '#BACKCOMP null');
      const metadata = {
        user_id: userId.toString(),
        session_id: sessionId,
      };
      console.log('[DEBUG] visualizeDpm - metadata:', metadata);

      const requestBody = {
        jsonrpc: "2.0",
        id: crypto.randomUUID(),
        method: "DPMVisualize",
        params: {
          metadata: metadata,
          message: {
            parts: [
              {
                type: "data",
                data: {
                  dpm_filename: dpmFilename,
                  ...(dpmUuid && { dpm_uuid: dpmUuid }),
                },
              },
            ],
          },
        },
      };

      // Use INTERPRETER_BASE_URL for all API calls (now internal service)
      const response = await fetch(INTERPRETER_BASE_URL ? `${INTERPRETER_BASE_URL}/api/a2a` : `/api/a2a`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("🔍 DPMVisualize response:", data);
      return data;
    } catch (error) {
      console.error("Error visualizing DPM:", error);
      throw error;
    }
  },

  /**
   * Send a DPM step navigation request to the backend
   * @param {number} stepIndex - The step index to navigate to (0-based)
   * @param {string} dpmFilename - The DPM filename
   * @param {string} userId - The user ID
   * @returns {Promise<Object>} - The response from the API
   */
  async navigateToStep(stepIndex, dpmFilename, userId, dpmUuid = null) {
    try {
      const metadata = {
        user_id: userId.toString(),
        session_id: getSessionId(userId),
      };

      const requestBody = {
        jsonrpc: "2.0",
        id: crypto.randomUUID(),
        method: "DPMStep",
        params: {
          metadata: metadata,
          message: {
            parts: [
              {
                type: "data",
                data: {
                  step_index: stepIndex,
                  dpm_filename: dpmFilename,
                  ...(dpmUuid && { dpm_uuid: dpmUuid }),
                },
              },
            ],
          },
        },
      };

      // Use INTERPRETER_BASE_URL for all API calls (now internal service)
      const response = await fetch(INTERPRETER_BASE_URL ? `${INTERPRETER_BASE_URL}/api/a2a` : `/api/a2a`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("🎯 DPMStep response:", data);
      return data;
    } catch (error) {
      console.error("Error navigating to step:", error);
      throw error;
    }
  },

  /**
   * Replay the current DPM step: snap instantly to the previous step's canonical pose, then re-run this step's animation.
   * Uses the same step_index as navigateToStep for the step being replayed (0-based API index).
   */
  async replayStep(stepIndex, dpmFilename, userId, dpmUuid = null) {
    try {
      const metadata = {
        user_id: userId.toString(),
        session_id: getSessionId(userId),
      };

      const requestBody = {
        jsonrpc: "2.0",
        id: crypto.randomUUID(),
        method: "DPMStep",
        params: {
          metadata: metadata,
          message: {
            parts: [
              {
                type: "data",
                data: {
                  step_index: stepIndex,
                  dpm_filename: dpmFilename,
                  replay: true,
                  ...(dpmUuid && { dpm_uuid: dpmUuid }),
                },
              },
            ],
          },
        },
      };

      const response = await fetch(INTERPRETER_BASE_URL ? `${INTERPRETER_BASE_URL}/api/a2a` : `/api/a2a`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("🔄 DPMStep replay response:", data);
      return data;
    } catch (error) {
      console.error("Error replaying step:", error);
      throw error;
    }
  },

  /**
   * Capture Unity viewport PNG via visualizer WS roundtrip (Capture.snapshot).
   * @param {string|number} userId
   * @param {{ width?: number, height?: number }} [opts]
   * @returns {Promise<{ mime_type: string, image_base64: string }>}
   */
  async captureCameraSnapshot(userId, opts = {}) {
    const baseUrl = VISUALIZER_BASE_URL?.replace(/\/$/, "");
    const sessionId = getSessionId(userId);
    const width = opts.width ?? 256;
    const height = opts.height ?? 256;
    const qs = new URLSearchParams({
      session_id: sessionId,
      width: String(width),
      height: String(height),
    });
    const url = baseUrl
      ? `${baseUrl}/api/camera-snapshot?${qs}`
      : `/api/camera-snapshot?${qs}`;
    const response = await fetch(url, { method: "POST" });
    if (!response.ok) {
      throw new Error(`camera-snapshot HTTP ${response.status}`);
    }
    return response.json();
  },

  /**
   * Forward a JSON-RPC notification to Unity (via interpreter A2A UnityCommand).
   * @param {string} jrpcMethod - Unity method, e.g. "Zoom.in", "Target.ids"
   * @param {Object} [jrpcParams] - Params object (default {})
   * @param {string} userId - User ID
   * @returns {Promise<Object>}
   */
  async sendUnityJrpc(jrpcMethod, jrpcParams = {}, userId) {
    try {
      const metadata = {
        user_id: userId.toString(),
        session_id: getSessionId(userId),
      };

      const requestBody = {
        jsonrpc: "2.0",
        id: crypto.randomUUID(),
        method: "UnityCommand",
        params: {
          metadata: metadata,
          message: {
            parts: [
              {
                type: "data",
                data: {
                  jrpc_method: jrpcMethod,
                  jrpc_params: jrpcParams && typeof jrpcParams === "object" ? jrpcParams : {},
                },
              },
            ],
          },
        },
      };

      const response = await fetch(INTERPRETER_BASE_URL ? `${INTERPRETER_BASE_URL}/api/a2a` : `/api/a2a`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log(`🎮 UnityCommand jrpc ${jrpcMethod}:`, data);
      return data;
    } catch (error) {
      console.error(`Error sending Unity jrpc ${jrpcMethod}:`, error);
      throw error;
    }
  },

  /**
   * Clear the SSE command queue in the visualizer service
   * This prevents old commands from being sent to new Unity instances
   * @returns {Promise<Object>} - The response from the API
   */
  async clearCommandQueue() {
    try {
      // Build URL - normalize trailing slash in VISUALIZER_BASE_URL
      const baseUrl = VISUALIZER_BASE_URL?.replace(/\/$/, '');
      const url = baseUrl
        ? `${baseUrl}/api/clear_command_queue`
        : `/api/clear_command_queue`;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("🧹 Command queue cleared:", data);
      return data;
    } catch (error) {
      console.error("Error clearing command queue:", error);
      throw error;
    }
  },


  /**
   * Clear the Unity scene by sending Fetch-clear command via WebSocket
   * @param {string} userId - The user ID
   * @returns {Promise<Object>} - The response from the API
   */
  async clearUnityScene(userId) {
    try {
      const sessionId = getSessionId(userId);
      const baseUrl = VISUALIZER_BASE_URL?.replace(/\/$/, '');
      const url = baseUrl
        ? `${baseUrl}/api/clear_unity_scene`
        : `/api/clear_unity_scene`;

      const requestBody = {
        session_id: sessionId || null,
      };

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log(`🧹 Unity scene cleared:`, data);
      return data;
    } catch (error) {
      console.error("Error clearing Unity scene:", error);
      throw error;
    }
  },

  /**
   * Initialize an empty Unity scene by sending Fetch-empty command via WebSocket
   * @param {string} userId - The user ID
   * @returns {Promise<Object>} - The response from the API
   */
  async emptyUnityTab(userId) {
    try {
      const sessionId = getSessionId(userId);
      const baseUrl = VISUALIZER_BASE_URL?.replace(/\/$/, '');
      const url = baseUrl
        ? `${baseUrl}/api/empty_unity_tab`
        : `/api/empty_unity_tab`;

      const requestBody = {
        session_id: sessionId || null,
      };

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log(`🔄 Empty Unity tab initialized:`, data);
      return data;
    } catch (error) {
      console.error("Error initializing empty Unity tab:", error);
      throw error;
    }
  },

  async transformPosition(x, y, z, userId = null) {
    try {
      const baseUrl = VISUALIZER_BASE_URL?.replace(/\/$/, '');
      const url = baseUrl
        ? `${baseUrl}/api/transform/position`
        : `/api/transform/position`;

      const requestBody = {
        x: x,
        y: y,
        z: z,
        session_id: getSessionId(userId),
      };

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log(`📍 Transform position sent:`, data);
      return data;
    } catch (error) {
      console.error("Error sending transform position:", error);
      throw error;
    }
  },

  async transformRotation(x, y, z, userId = null) {
    try {
      const baseUrl = VISUALIZER_BASE_URL?.replace(/\/$/, '');
      const url = baseUrl
        ? `${baseUrl}/api/transform/rotation`
        : `/api/transform/rotation`;

      const requestBody = {
        x: x,
        y: y,
        z: z,
        session_id: getSessionId(userId),
      };

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log(`🔄 Transform rotation sent:`, data);
      return data;
    } catch (error) {
      console.error("Error sending transform rotation:", error);
      throw error;
    }
  },

  /**
   * Get a part's position and rotation at a given step (from the loaded procedure).
   * Used by the animation panel to update target fields when the user clicks a part.
   * @param {string} partId - Part ID
   * @param {number} stepIndex - Step index (0-based)
   * @returns {Promise<{ position: { x, y, z }, rotation: { x, y, z } }>} - The part's transform at that step
   */
  async getPartTransformAtStep(partId, stepIndex) {
    try {
      const baseUrl = VISUALIZER_BASE_URL?.replace(/\/$/, "");
      const params = new URLSearchParams({
        part_id: String(partId),
        step_index: String(stepIndex),
        session_id: getSessionId(),
      });
      const url = baseUrl
        ? `${baseUrl}/api/part_transform?${params}`
        : `/api/part_transform?${params}`;

      const response = await fetch(url, { method: "GET" });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching part transform at step:", error);
      throw error;
    }
  },

  /**
   * Bulk part transforms at a step (single request for multi-select).
   * @param {string[]} partIds
   * @param {number} stepIndex
   * @returns {Promise<{ transforms: Record<string, { position, rotation }> }>}
   */
  async getPartTransformsAtStep(partIds, stepIndex) {
    const normalized = (partIds || [])
      .map(id => String(id).trim())
      .filter(Boolean);
    if (normalized.length === 0) {
      return { transforms: {} };
    }

    try {
      const baseUrl = VISUALIZER_BASE_URL?.replace(/\/$/, "");
      const url = baseUrl
        ? `${baseUrl}/api/part_transforms`
        : `/api/part_transforms`;

      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          part_ids: normalized,
          step_index: stepIndex,
          session_id: getSessionId(),
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching part transforms at step:", error);
      throw error;
    }
  },

  async getValidTransformsAtStep(parts, stepIndex) {
    const partIds = (parts || []).map(p => String(p.id));
    const { transforms } = await this.getPartTransformsAtStep(partIds, stepIndex);
    return partIds
      .map(id => transforms[id])
      .filter(t => t?.position && t?.rotation);
  },

  async transformTargets(targetIds, userId = null) {
    try {
      const baseUrl = VISUALIZER_BASE_URL?.replace(/\/$/, '');
      const url = baseUrl
        ? `${baseUrl}/api/transform/targets`
        : `/api/transform/targets`;

      const requestBody = {
        target_ids: targetIds,
        session_id: getSessionId(userId),
      };

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log(`🎯 Transform targets sent:`, data);
      return data;
    } catch (error) {
      console.error("Error sending transform targets:", error);
      throw error;
    }
  },

  async unselectPartLabel(userId = null) {
    try {
      const baseUrl = VISUALIZER_BASE_URL?.replace(/\/$/, '');
      const url = baseUrl
        ? `${baseUrl}/api/unselect_part_label`
        : `/api/unselect_part_label`;

      const requestBody = {
        session_id: getSessionId(userId),
      };

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("🔍 PartLabel unselected:", data);
      return data;
    } catch (error) {
      console.error("Error unselecting part label:", error);
      throw error;
    }
  },

  /** Remove runtime-only visible_part_ids from DPM so they are not persisted. */
  stripVisiblePartIdsFromDpm(dpmData) {
    if (!dpmData) return dpmData;
    const stripFromNode = (node) => {
      if (!node) return node;
      const { visible_part_ids, ...rest } = node;
      const n = { ...rest };
      if (Array.isArray(n.children)) n.children = n.children.map(stripFromNode);
      return n;
    };
    const out = { ...dpmData };
    if (out.behavior_tree) {
      const bt = out.behavior_tree;
      out.behavior_tree =
        bt?.behavior_tree != null
          ? { ...bt, behavior_tree: stripFromNode(bt.behavior_tree) }
          : stripFromNode(bt);
    }
    return out;
  },

  /**
   * Save DPM to datastore. Strips runtime visible_part_ids before sending.
   * @param {string} dpmFilename
   * @param {Object} dpmData - The complete DPM data to save
   * @param {string} userId - The user ID
   * @param {string|null} [dpmUuid] - Optional DPM UUID
   * @returns {Promise<Object>} - The response from the API
   */
  async saveDpmToDatastore(dpmFilename, dpmData, userId, dpmUuid = null) {
    try {
      const userIdToUse = userId?.id || userId;
      const userIdString = userIdToUse?.id || userIdToUse;

      const metadata = {
        user_id: userIdString.toString(),
        session_id: getSessionId(userId),
      };

      const dpmDataToSave = this.stripVisiblePartIdsFromDpm(dpmData);

      const requestBody = {
        jsonrpc: "2.0",
        id: crypto.randomUUID(),
        method: "DPMUpdate",
        params: {
          metadata: metadata,
          message: {
            parts: [
              {
                type: "data",
                data: {
                  dpm_filename: dpmFilename,
                  dpm_data: dpmDataToSave,
                  ...(dpmUuid && { dpm_uuid: dpmUuid }),
                },
              },
            ],
          },
        },
      };

      const response = await fetch(INTERPRETER_BASE_URL ? `${INTERPRETER_BASE_URL}/api/a2a` : `/api/a2a`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("💾 DPMUpdate response:", data);
      return data;
    } catch (error) {
      console.error("Error saving DPM to datastore:", error);
      throw error;
    }
  },
};
